import { useCallback, useContext } from 'react';

import { RouterContext, useQuery } from '../../../contexts/RouterContext';

type RevisionQueryParams = {
  revisionScanId?: number;
};

type UseRevisionScanIdQueryParamReturn = {
  /**
   * The current `revisionScanId` query param.
   */
  revisionScanId?: number;
  /**
   * Function which will set the `revisionScanId` query param.
   * Any other query params will be preserved.
   * @param {number} [revisionScanId] - The revision scan ID to set. If not provided, the query param will be removed.
   */
  setRevisionScanId: (revisionScanId?: number) => void;
};

/**
 * Helper for managing the `revisionScanId` query param.
 * @example
 * ```typescript
 * const { revisionScanId, setRevisionScanId } = useRevisionScanIdQueryParam();
 * ```
 */
export function useRevisionScanIdQueryParam(): UseRevisionScanIdQueryParamReturn {
  const { getQuery } = useContext(RouterContext);

  //  Query params are strings, so we parse it out to a number
  //  We also guard against random strings/invalid numbers
  const [query, setQuery] = useQuery<RevisionQueryParams>((parserQuery) => {
    if (!parserQuery.revisionScanId) {
      return { revisionScanId: undefined };
    }
    const revisionScanId = Number(parserQuery.revisionScanId);
    if (!Number.isFinite(revisionScanId)) {
      return { revisionScanId: undefined };
    }
    return { revisionScanId };
  });

  return {
    revisionScanId: query.revisionScanId,
    setRevisionScanId: useCallback((revisionScanId?: number) => {
      // Get current query params to preserve them
      // Note that we don't use the `query` from `useQuery`, since it would cause an infinite loop.
      const currentQuery = getQuery();
      setQuery({
        ...currentQuery,
        revisionScanId,
      });
    }, [getQuery, setQuery]),
  };
}
